import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
  Fab,
  Zoom,
  Skeleton,
} from "@mui/material";
import {
  Search,
  GridView,
  List as ListIcon,
  KeyboardArrowUp,
  FilterList,
  School,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

// Components
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import Catalog from "../Components/CoursePages/Catalog";
import Catalog2 from "../Components/CoursePages/Catalog2";
import Blogs from "../Components/CoursePages/Blogs";
import StudentSidebar from "./StudentSdebar";

const API_BASE = "https://tdtlworld.com/dss-kaas-backend/";

export default function CoursePages() {
  const theme = useTheme();
  const [viewType, setViewType] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("jwttoken");
    const fetchData = async () => {
      setLoading(true);
      try {
        const [coursesRes, categoriesRes] = await Promise.all([
          axios.get(`${API_BASE}lms/courses/`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE}lms/categories/`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setCourses(coursesRes.data.courses || []);
        setCategories(categoriesRes.data || []);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const goToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const filteredCourses = courses.filter((course) => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || (course.categories && course.categories.includes(selectedCategory));
    const matchesEnrollment = filterType === "all" || (filterType === "enrolled" && course.enrolled);
    return matchesSearch && matchesCategory && matchesEnrollment;
  });

  const SkeletonLoader = () => (
    <Grid container spacing={4}>
      {[1, 2, 3].map((n) => (
        <Grid item xs={12} sm={6} md={4} key={n}>
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '24px' }}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '16px', mb: 2 }} />
            <Skeleton variant="text" height={32} width="80%" />
            <Skeleton variant="text" height={20} width="60%" />
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Header />
      
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <StudentSidebar onToggle={setSidebarExpanded} />
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: { xs: 2, md: 4, lg: 6 },
            width: sidebarExpanded ? 'calc(100% - 260px)' : 'calc(100% - 80px)',
            transition: theme.transitions.create('width', { duration: 300 }),
            height: 'calc(100vh - 64px)',
            overflowY: 'auto'
          }}
        >
          <Container maxWidth="xl">
            {/* Page Title & Search Section */}
            <Box sx={{ mb: 8, textAlign: 'center' }}>
              <Box sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 2, 
                bgcolor: alpha('#D4AF37', 0.1), 
                px: 4, 
                py: 1.5, 
                borderRadius: '50px',
                border: '1px solid',
                borderColor: alpha('#D4AF37', 0.3),
                mb: 3
              }}>
                <School sx={{ color: '#D4AF37', fontSize: '2rem' }} />
                <Typography variant="h5" fontWeight={900} sx={{ color: '#D4AF37', textTransform: 'uppercase', letterSpacing: '2px' }}>
                  Course House
                </Typography>
              </Box>
              
              <Typography variant="h2" fontWeight={900} gutterBottom sx={{ 
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, #D4AF37)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 4
              }}>
                Explore Your Future
              </Typography>

              <Paper elevation={0} sx={{ 
                p: 1, 
                maxWidth: '800px', 
                mx: 'auto', 
                borderRadius: '50px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(10px)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
              }}>
                <TextField
                  fullWidth
                  placeholder="What would you like to learn today?"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#D4AF37', ml: 2 }} />
                      </InputAdornment>
                    ),
                    sx: {
                      '& fieldset': { border: 'none' },
                      fontSize: '1.2rem',
                      fontWeight: 600
                    }
                  }}
                />
              </Paper>
            </Box>

            {/* Filter Controls Row */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 6, 
                borderRadius: '24px', 
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                border: '1px dashed',
                borderColor: 'divider'
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Course Access"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    InputProps={{
                      startAdornment: <FilterList sx={{ mr: 1, color: 'primary.main' }} />
                    }}
                  >
                    <MenuItem value="all">All Available Courses</MenuItem>
                    <MenuItem value="enrolled">My Enrolled Courses</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Category"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <MenuItem value="">All Specializations</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.category_id} value={cat.name}>{cat.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
                  <ToggleButtonGroup
                    value={viewType}
                    exclusive
                    onChange={(e, next) => next && setViewType(next)}
                    sx={{ bgcolor: 'background.paper', borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}
                  >
                    <ToggleButton value="grid" sx={{ px: 3 }}>
                      <GridView sx={{ mr: 1 }} /> Grid View
                    </ToggleButton>
                    <ToggleButton value="list" sx={{ px: 3 }}>
                      <ListIcon sx={{ mr: 1 }} /> List View
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </Paper>

            {/* Course Grid/List */}
            <AnimatePresence mode="wait">
              {loading ? (
                <SkeletonLoader key="loading" />
              ) : (
                <motion.div
                  key={viewType + filteredCourses.length}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <Grid container spacing={4}>
                    {filteredCourses.length > 0 ? (
                      filteredCourses.map((course) => (
                        <Grid item xs={12} md={viewType === 'grid' ? 4 : 12} key={course.course_id}>
                          {viewType === 'grid' ? (
                            <Catalog
                              cover_img={course.cover_img}
                              coursetitle={course.title}
                              courseId={course.course_id}
                              price={course.price}
                              instructor={course.instructor.name}
                              isWishlisted={course.is_wishlisted}
                            />
                          ) : (
                            <Catalog2
                              coursetitle={course.title}
                              cover_img={course.cover_img}
                              courseId={course.course_id}
                              description={course.description}
                              numlesson={course.parts_count || 0}
                              start_date={new Date(course.start_date).toLocaleDateString()}
                              instructor={course.instructor.name}
                              isWishlisted={course.is_wishlisted}
                              price={course.price}
                            />
                          )}
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', py: 10 }}>
                          <Typography variant="h5" color="text.secondary">No courses found matching your criteria.</Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Extra Content */}
            <Blogs />
            
            <Footer />
          </Container>
        </Box>
      </Box>

      {/* Back to Top */}
      <Zoom in={showBackToTop}>
        <Fab 
          color="primary" 
          size="large" 
          onClick={goToTop}
          sx={{ 
            position: 'fixed', 
            bottom: 40, 
            right: 40, 
            boxShadow: '0 8px 25px rgba(212, 175, 55, 0.3)' 
          }}
        >
          <KeyboardArrowUp />
        </Fab>
      </Zoom>
    </Box>
  );
}
